import { Grid, Stack, Typography } from '@mui/material';

import { ChangeEvent, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setDetailPaneTabIndex,
	setIsReloadTableData,
} from 'common/store/reducer/promptBuilderReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { addPrompt } from '../../api';
import { IPromptBuilderParams } from '../../api/type-def';
import { PROMPT_TITLE_UNIQUE_ERROR } from '../../PromptBuilderDetails/PromptBuilderConstants';
import promptBuilderStyles from '../../PromptBuilderLayout.module.scss';

import actionStyles from '../PromptActions.module.scss';

interface IPromptClone {
	closeCloneModal: () => void;
}
const PromptClone = (props: IPromptClone) => {
	const dispatch = useDispatch();

	const { closeCloneModal } = props;

	const { OK, CONFLICT } = HTTP_STATUS_CODES;

	const [cloneTitle, setCloneTitle] = useState<string>('');
	const [cloneError, setCloneError] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const selectedPromptData: IPromptBuilderParams | null = useSelector(
		(state: RootState) => state.promptBuilder.selectedPrompt,
	);

	const titleChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
		if (cloneError) setCloneError(false);
		setCloneTitle(event.target.value);
	};

	const handleCloneAction = async () => {
		setIsLoading(true);
		const parentPrompt = selectedPromptData?.key;
		const clonedPromptData: any = { ...selectedPromptData };
		delete clonedPromptData.key;
		clonedPromptData.is_draft = true;
		clonedPromptData.is_active = false;

		const res = await addPrompt(cloneTitle, clonedPromptData);
		if (res?.statusCode === OK) {
			setIsLoading(false);
			dispatch(
				setToastMessage({
					message: `Successfully Cloned the prompt ${parentPrompt}`,
					severity: 'success',
					isToastOpen: true,
				}),
			);
			dispatch(setIsReloadTableData(true));
			dispatch(setDetailPaneTabIndex(0));
			closeCloneModal();
		} else if (res?.statusCode === CONFLICT) {
			setCloneError(true);
			setIsLoading(false);
		} else {
			const { errorBody } = res;
			const responseError: any = res.error;
			const errorMessage =
				(errorBody?.error as string) || (responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setIsLoading(false);
		}
	};

	return (
		<Grid
			container
			direction="column"
			spacing={2}
			sx={{ width: '100%', boxSizing: 'border-box' }}
		>
			<Stack
				className={actionStyles['prompt-modal-title-container']}
				onClick={(event) => event.stopPropagation()}
				data-testid="title-container"
			>
				<Grid item>
					<Typography className={actionStyles['prompt-modal-title']}>
						Duplicate prompt
					</Typography>
				</Grid>
				<CloseIcon
					fontSize="medium"
					onClick={closeCloneModal}
					className={actionStyles['prompt-modal-close']}
					data-testid="close-icon"
				/>
			</Stack>
			<Grid item>
				<Typography>{PROMPT_TITLE_UNIQUE_ERROR}</Typography>
			</Grid>
			<Grid item>
				<TextFieldWithLabel
					label={toSentenceCase('Title')}
					name="clone-title"
					id="clone-title"
					value={cloneTitle}
					disabled={isLoading}
					inputProps={{
						'data-testid': 'input-clone-title',
					}}
					onChange={titleChangeHandler}
					error={!!cloneError}
					required
					helperText={
						cloneError
							? 'Title already exists, please enter a valid title'
							: null
					}
					className={promptBuilderStyles['clone-title-width']}
					positionClass={promptBuilderStyles['word-limit-position']}
					parentClass={promptBuilderStyles['word-txt-position']}
					wordLimit={420}
				/>
			</Grid>
			<Grid item>
				<Grid container justifyContent="flex-end" spacing={2}>
					<Grid item>
						<ButtonComp
							btnType="secondary"
							onClick={closeCloneModal}
							data-testid="cancel-dialog-btn"
						>
							Cancel
						</ButtonComp>
					</Grid>
					<Grid item>
						<ButtonComp
							btnType="primary"
							loading={isLoading}
							onClick={handleCloneAction}
							disabled={!cloneTitle || cloneError}
							data-testid="clone-dialog-btn"
						>
							Duplicate
						</ButtonComp>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default PromptClone;
